import { defineComponent as _defineComponent } from 'vue'
import PictureComp from '@/components/picture/PictureComp.vue';
import TitleScoreList from '@/components/TitleScoreList.vue';
import Vue, { computed, inject, ref } from 'vue';
import ProviderIcon from '@/components/picture/ProviderIcon.vue';
import { ThumborOptions } from '@/filters/thumbor';
import PromotionRowCTA from '@/components/buybox-new/buyboxoffer/PromotionRowCTA.vue';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { useLanguageStore } from '@/helpers/composables/useStores';
import ClickoutAnchor from '@/components/nav/links/ClickoutAnchor.vue';
import { ScoringProvider } from '@/helpers/scoring-helper';
import { ClickoutUrl } from '@/helpers/clickout-helper';
import { SnowplowContext, SnowplowModuleContext, SnowplowTitleContextGraphql } from '@/helpers/tracking/providers';
import { BuyboxParam, OfferLocation } from '@/helpers/offer-helper';
import { BuyBoxFilterOptions } from '@/stores/user.store';
import { getClickoutTrackingPlacement } from '@/helpers/composables/useProvideClickoutPlacement';
import { CatalogueItem } from '@/helpers/promotion-helper';
import { TITLE_DETAIL_MODULE_TECHNICAL_NAME } from '@/enums/module-template';
import { observableOptions, useOfferImpressionTracking } from '@/helpers/composables/useOfferImpressionTracking';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { useI18n } from '@/helpers/composables/useI18n';
import { useQTVCompositionId } from '@/components/buybox/QualityTvRow/useQualityTv';
import { useSingleQTV } from '@/components/buybox-new/composables/useSingleQTV';
import ImpressionPixel from '@/components/sponsored-recommendations/ImpressionPixel.vue';
import { getDisneyImpressionUrl } from '@/helpers/provider-promotion';
import DisclaimerButton from '@/components/buybox-new/DisclaimerButton.vue';
import { useExperiment } from '@/libraries/experiment';
import { usePPExperiments } from '@/components/experiments/PPExperiments';


export default /*#__PURE__*/_defineComponent({
  __name: 'PPExpSQTV',
  props: {
    title: null,
    catalogueItem: null,
    additionalContexts: null,
    sqtvProviderShortName: { default: null }
  },
  setup(__props: any) {

const props = __props as { title: TitleDetail, catalogueItem: CatalogueItem | null, additionalContexts: SnowplowContext[], sqtvProviderShortName: string | null };



const { activeVariant } = useExperiment('PP_SQTV_EXP');

const { country, webLocale } = useLanguageStore();
const { tc } = useI18n();
const { clickoutType, placement } = getClickoutTrackingPlacement('sqtv');

/** impression tracking for Disney+ */
const mountImpressionPixel = ref(false);

const providerShortName = computed(() => props.sqtvProviderShortName ?? props.catalogueItem?.shortName ?? '');

const titleContext = inject('titleContext', ref<SnowplowTitleContextGraphql | undefined>(undefined));
const filterOption = inject('filterOption', ref<BuyBoxFilterOptions | undefined>(undefined));

const isDisneyPlusSQTV = computed(() => providerShortName.value === 'dnp');
const disneyPlusImpressionPixel = computed(() => {
	return isDisneyPlusSQTV.value ? getDisneyImpressionUrl(337, webLocale.value) : null;
});

const excludedScores = computed(() => {
	const excluded = [];
	if ((scores.value.imdbScore || 0) < 7) {
		excluded.push(ScoringProvider.IMDB);
	}
	if ((scores.value.jwRating || 0) < 0.7) {
		excluded.push(ScoringProvider.TMDB);
	}
	if ((scores.value.tomatoMeter || 0) < 70) {
		excluded.push(ScoringProvider.ROTTEN_TOMATOES);
	}
	return excluded.length === 3 ? excluded.slice(0, 1) : excluded;
});

const { similarTitle, loading, sqtvBannerAndDisclaimer } = useSingleQTV(() => props.title, providerShortName, true);

const sourcePosterUrl = computed(() => props.title?.content?.posterUrl || '');
const posterUrl = computed(() => similarTitle.value?.node?.content?.posterUrl || '');
const backdropUrl = computed(() => similarTitle.value?.node?.content?.backdrops?.[0].backdropUrl || '');
const scores = computed(() => similarTitle.value?.node?.content?.scoring || {});
const votesNumber = computed(() => similarTitle.value?.node?.content?.interactions.votesNumber || 0);
const offer = computed(() => {
	const offers = similarTitle.value?.node.offers || [];
	return offers[0];
});
const offerEntityId = computed(() => similarTitle.value?.node.id ?? 'nsid');

const { getQTVCompositionId } = useQTVCompositionId();

const compositionId = computed(() => {
	const sourceTitleID = props.title.id;
	const similarTitleID = similarTitle.value?.node.id ?? 'nsid';
	const shortName = providerShortName.value === 'amp' ? 'prv' : providerShortName.value;
	return getQTVCompositionId(shortName, sourceTitleID, similarTitleID, 'sqtv');
});

const moduleContext = computed(
	() => new SnowplowModuleContext('', -1, TITLE_DETAIL_MODULE_TECHNICAL_NAME.SINGLE_QUALTIY_TV, '')
);

const recommenderContext = computed(() => {
	const seasonNumber = props.title.__typename === 'Season' ? props.title.content.seasonNumber : null;
	const titleId = props.title.__typename === 'Season' ? props.title.show.id : props.title.id;

	return TrackingHelper.getRecommenderContext('sqtv', titleId, seasonNumber, null, compositionId.value);
});

const similarTitleContext = computed(() => {
	if (!similarTitle.value) return null;
	return new SnowplowTitleContextGraphql(
		similarTitle.value.node.objectId,
		similarTitle.value.node.objectType,
		null,
		null,
		null,
		0
	);
});

const { setPPExperimentTags } = usePPExperiments('sqtv');

const clickoutUrl = computed(() => {
	if (!offer.value) return null;

	const clickoutUrl = new ClickoutUrl(offer)
		.set('uct_country', country.value)
		.set('uct_buybox', BuyboxParam.Normal)
		.set('uct_ft', '1')
		.addContexts(moduleContext.value)
		.addContexts(similarTitleContext.value)
		.setForTracking('offerLocation', OfferLocation.FREE_TRIAL)
		.updateContext('ClickoutContextGraphql', 'filterOption', filterOption.value)
		.updateContext('ClickoutContextGraphql', 'placement', placement)
		.updateContext('ClickoutContextGraphql', 'clickoutType', clickoutType)
		.setTitleSourceId(props.title.id)
		.updateContext('TitleContextGraphql', 'offerEntityId', offerEntityId.value)
		.addContexts(recommenderContext.value);

	clickoutUrl.apply(setPPExperimentTags);

	return clickoutUrl;
});

const tags = computed(() => {
	const tags = similarTitle.value?.node?.content?.tags?.map(tag => tag.translatedName) ?? [];
	const genres = similarTitle.value?.node?.content?.genres?.map(tag => tag.translation) ?? [];

	return [tc('WEBAPP_HIGHLY_RATED'), ...tags, ...genres, tc('WEBAPP_FREE_TRIAL_TAG')].filter(Boolean).slice(0, 3);
});

/* VISIBILITY TRACKING */

const observeVisibilityOptions = computed(() => ({
	...observableOptions,
	callback: (isVisible: boolean) => onVisibilityChanged(isVisible),
}));

const baseContexts = computed(() => [...props.additionalContexts, moduleContext.value, recommenderContext.value]);
const recommenderImpressionContexts = computed(() =>
	similarTitleContext.value ? [...baseContexts.value, similarTitleContext.value] : baseContexts.value
);

const offerImpressionContexts = computed(() =>
	titleContext.value ? [...baseContexts.value, titleContext.value] : baseContexts.value
);

const { onIntersection } = useOfferImpressionTracking({
	additionalContexts: offerImpressionContexts,
	groupId: 'sqtv',
});

function onVisibilityChanged(isVisible: boolean) {
	if (!isVisible) return;

	if (!mountImpressionPixel.value) {
		mountImpressionPixel.value = true;
	}

	// ML Recommender (similar titles) impression tracking
	TrackingHelper.trackEvent(
		'recommender_feature',
		{
			action: 'impression',
			value: 1,
			nonInteraction: true,
		},
		recommenderImpressionContexts.value
	);
	if (clickoutUrl.value) {
		onIntersection(clickoutUrl.value, isVisible);
	}
}

/* VISIBILITY TRACKING */

function imageUrl(titlePosterUrl: string | null | undefined, type: 'poster' | 'backdrop' = 'poster') {
	if (!titlePosterUrl) {
		return undefined;
	}
	return Vue.filter('Thumbor')({
		url: titlePosterUrl,
		imageType: type,
	} as ThumborOptions)?.replace('{format}', 'jpg');
}

return { __sfc: true,props, activeVariant, country, webLocale, tc, clickoutType, placement, mountImpressionPixel, providerShortName, titleContext, filterOption, isDisneyPlusSQTV, disneyPlusImpressionPixel, excludedScores, similarTitle, loading, sqtvBannerAndDisclaimer, sourcePosterUrl, posterUrl, backdropUrl, scores, votesNumber, offer, offerEntityId, getQTVCompositionId, compositionId, moduleContext, recommenderContext, similarTitleContext, setPPExperimentTags, clickoutUrl, tags, observeVisibilityOptions, baseContexts, recommenderImpressionContexts, offerImpressionContexts, onIntersection, onVisibilityChanged, imageUrl, PictureComp, TitleScoreList, ProviderIcon, PromotionRowCTA, ClickoutAnchor, ImpressionPixel, DisclaimerButton }
}

})